import { apiClient } from "./apiClient";
import { unwrapApiResponse } from "../shared/utils/apiResponse";

export type TrialBalanceRow = {
  ledger: string;
  debit: number;
  credit: number;
  balance: number;
};

export type LedgerVoucherRow = {
  id?: number;
  ledgerId?: number;
  accountLedgerId?: number;
  ledgerName?: string;
  name?: string;
  accountGroup?: string;
  debitTotal?: number;
  creditTotal?: number;
  debit?: number;
  credit?: number;
  balance?: number;
  closingBalance?: number;
  balanceType?: string;
  facilityType?: string;
  overdraftLimit?: number;
  drawingPower?: number;
  allowOverLimit?: boolean;
  overLimitTolerance?: number;
  availableOverdraftLimit?: number;
};

export type LedgerMonthRow = {
  year?: number;
  month?: number;
  monthName?: string;
  debitTotal?: number;
  creditTotal?: number;
  debit?: number;
  credit?: number;
  closingBalance?: number;
  balanceType?: string;
};

export type LedgerTransactionRow = {
  id?: number;
  entryDate?: string;
  date?: string;
  voucherType?: string;
  narration?: string;
  description?: string;
  particulars?: string;
  voucherRef?: string;
  sourceModule?: string;
  sourceId?: number;
  debit?: number;
  credit?: number;
  amount?: number;
  runningBalance?: number;
  balanceType?: string;
};

export type StatementLineRow = {
  ledger?: string;
  balance?: number;
};

export type StatementGroupRow = {
  groupName: string;
  total: number;
  ledgers?: Array<{
    ledgerId?: number;
    ledgerName?: string;
    accountGroup?: string;
    amount?: number;
  }>;
};

export type ProfitLossResult = {
  income: StatementGroupRow[];
  expense: StatementGroupRow[];
  netProfit: number;
};

export type BalanceSheetResult = {
  assets: StatementGroupRow[];
  liabilities: StatementGroupRow[];
  netProfit: number;
};

export type PdfReportType = {
  reportType: string;
  title: string;
  description?: string;
  icon?: string;
};

export async function getTrialBalance(params: {
  organizationId: string | number;
  financialYearId: string | number;
  from?: string;
  to?: string;
}) {
  const { data } = await apiClient.get<TrialBalanceRow[]>("/reports/trial-balance", { params });
  return Array.isArray(data) ? data : [];
}

export async function getLedgerVoucherSummary() {
  const { data } = await apiClient.get("/reports/ledger-vouchers");
  const rows = unwrapApiResponse<LedgerVoucherRow[]>(data);
  return Array.isArray(rows) ? rows : [];
}

export async function getLedgerVoucherMonthly(ledgerId: number) {
  const { data } = await apiClient.get(`/reports/ledger-vouchers/${ledgerId}/monthly`);
  const rows = unwrapApiResponse<LedgerMonthRow[]>(data);
  return Array.isArray(rows) ? rows : [];
}

export async function getLedgerVoucherTransactions(ledgerId: number, params?: { year?: number; month?: number; fromDate?: string; toDate?: string }) {
  const { data } = await apiClient.get(`/reports/ledger-vouchers/${ledgerId}/transactions`, { params });
  const rows = unwrapApiResponse<LedgerTransactionRow[]>(data);
  return Array.isArray(rows) ? rows : [];
}

export async function getProfitLoss(params: {
  organizationId: string | number;
  financialYearId: string | number;
  from?: string;
  to?: string;
}) {
  const { data } = await apiClient.get<ProfitLossResult>("/reports/profit-loss", { params });
  return {
    income: Array.isArray(data?.income) ? data.income : [],
    expense: Array.isArray(data?.expense) ? data.expense : [],
    netProfit: Number(data?.netProfit ?? 0)
  };
}

export async function getBalanceSheet(params: {
  organizationId: string | number;
  financialYearId: string | number;
  asOnDate?: string;
}) {
  const { data } = await apiClient.get<BalanceSheetResult>("/reports/balance-sheet", { params });
  return {
    assets: Array.isArray(data?.assets) ? data.assets : [],
    liabilities: Array.isArray(data?.liabilities) ? data.liabilities : [],
    netProfit: Number(data?.netProfit ?? 0)
  };
}

export async function getLedgerStatement(ledgerId: number, params: {
  organizationId: string | number;
  financialYearId: string | number;
  from?: string;
  to?: string;
}) {
  const { data } = await apiClient.get(`/reports/ledger/${ledgerId}`, { params });
  return Array.isArray(data) ? data as LedgerTransactionRow[] : [];
}

export async function getPdfReportTypes() {
  const { data } = await apiClient.get("/pdf-reports/types");
  const rows = unwrapApiResponse<PdfReportType[]>(data);
  return Array.isArray(rows) ? rows : [];
}

export async function generatePdfReport(reportType: string, payload: Record<string, unknown>, download = false) {
  const { data } = await apiClient.post(`/pdf-reports/${reportType}${download ? "/download" : ""}`, payload, {
    responseType: "blob"
  });
  return data as Blob;
}
